import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Fire,
  CheckCircle,
  Exam,
  Trophy,
  Star,
  Lock,
  Medal
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  fire: Fire,
  check: CheckCircle,
  exam: Exam,
  trophy: Trophy,
  star: Star,
};

const CATEGORY_LABELS = {
  streak: 'Study Streaks',
  questions: 'Questions Answered',
  exam: 'Mock Exams',
};

const CATEGORY_ORDER = ['streak', 'questions', 'exam'];

export const AchievementsPage = () => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const { data } = await axios.get(`${API}/badges`, { withCredentials: true });
        setBadges(data);
      } catch (error) {
        console.error('Failed to fetch badges:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBadges();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const earnedCount = badges.filter(b => b.earned).length;
  const grouped = CATEGORY_ORDER.map(cat => ({
    category: cat,
    label: CATEGORY_LABELS[cat],
    badges: badges.filter(b => b.category === cat),
  }));

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="achievements-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Your Progress
          </p>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">
            Achievements
          </h1>
        </div>

        {/* Summary */}
        <div className="bg-card border border-border rounded-sm p-6 mb-8 flex items-center gap-6">
          <div className="w-16 h-16 rounded-sm bg-accent/20 flex items-center justify-center flex-shrink-0">
            <Medal weight="fill" className="w-9 h-9 text-accent" />
          </div>
          <div>
            <p className="font-heading font-black text-3xl tracking-tighter">
              {earnedCount}
              <span className="text-lg text-muted-foreground font-normal ml-1">/ {badges.length}</span>
            </p>
            <p className="text-sm text-muted-foreground">badges earned</p>
          </div>
        </div>

        {/* Badge Groups */}
        {grouped.map(group => (
          <div key={group.category} className="mb-8" data-testid={`badge-group-${group.category}`}>
            <h2 className="font-heading font-bold text-lg mb-4 tracking-tight">
              {group.label}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {group.badges.map(badge => (
                <BadgeCard key={badge.id} badge={badge} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BadgeCard = ({ badge }) => {
  const Icon = ICON_MAP[badge.icon] || Star;
  const earned = badge.earned;

  return (
    <div
      className={cn(
        "relative bg-card border rounded-sm p-4 text-center transition-all",
        earned
          ? "border-accent/50 shadow-sm"
          : "border-border opacity-50 grayscale"
      )}
      data-testid={`badge-${badge.id}`}
    >
      {!earned && (
        <Lock weight="fill" className="absolute top-2 right-2 w-4 h-4 text-muted-foreground" />
      )}
      <div className={cn(
        "w-12 h-12 mx-auto rounded-sm flex items-center justify-center mb-3",
        earned ? "bg-accent/20" : "bg-muted"
      )}>
        <Icon
          weight="fill"
          className={cn("w-7 h-7", earned ? "text-accent" : "text-muted-foreground")}
        />
      </div>
      <p className="font-heading font-bold text-sm mb-1">{badge.name}</p>
      <p className="text-xs text-muted-foreground leading-tight">{badge.description}</p>
      {earned && badge.earned_at && (
        <p className="text-[10px] text-accent mt-2">
          {new Date(badge.earned_at).toLocaleDateString()}
        </p>
      )}
    </div>
  );
};
